/**
 * Hierarchical Service Data Service for Customer Portal
 *
 * Supports multi-level service structure:
 * Category → Subcategory → Service Variations
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { fileToWebImageFile } from '../utils/imageForAI';

export interface ServiceVariation {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  price: number;
  duration: number; // in minutes
  active: boolean;
  category?: string; // Custom category for multi-industry support
  emergencyEligible?: boolean; // Can be used for emergency/walk-in bookings
  allowEmergencySqueeze?: boolean; // Can be squeezed into gaps (default true, OFF for fades/shaves)
}

export interface ServiceSubcategory {
  id: string;
  name: string;
  description: string;
  variations: ServiceVariation[];
  active: boolean;
}

export interface ServiceCategory {
  id: string;
  name: string;
  description: string;
  icon: string; // emoji or icon identifier

  // 🎯 FLEXIBLE STRUCTURE: Support both flat and hierarchical
  isFlat?: boolean;  // TRUE = show variations directly (Beard Services, Specialty Services)
  variations?: ServiceVariation[];  // Direct variations (for flat categories)
  subcategories?: ServiceSubcategory[];  // Subcategories (for hierarchical like Haircuts)

  active: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface FlatService {
  id: string;
  categoryName: string;
  subcategoryName: string;
  variationName: string;
  description: string;
  price: number;
  duration: number;
  imageUrl?: string;
  active: boolean;
}

class HierarchicalServiceDataService {
  private categoriesCollection = collection(db, 'service_categories');

  /**
   * Get all service categories (for admin - includes inactive)
   */
  async getAllCategories(): Promise<ServiceCategory[]> {
    const snapshot = await getDocs(this.categoriesCollection);

    return snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      } as ServiceCategory))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /**
   * Get only active service categories (for customer portal)
   */
  async getActiveCategories(): Promise<ServiceCategory[]> {
    const allCategories = await this.getAllCategories();

    // Filter to only active categories with active subcategories and variations
    return allCategories
      .filter(cat => cat.active)
      .map(cat => ({
        ...cat,
        subcategories: cat.subcategories
          ?.filter(sub => sub.active)
          .map(sub => ({
            ...sub,
            variations: sub.variations.filter(v => v.active)
          }))
          .filter(sub => sub.variations.length > 0) // Remove subcategories with no active variations
      }))
      .filter(cat => cat.subcategories && cat.subcategories.length > 0); // Remove categories with no active subcategories
  }

  /**
   * Get category by ID with full subcategories and variations
   */
  async getCategoryById(categoryId: string): Promise<ServiceCategory | null> {
    const docRef = doc(this.categoriesCollection, categoryId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return null;

    return {
      id: docSnap.id,
      ...docSnap.data(),
      createdAt: docSnap.data().createdAt?.toDate() || new Date(),
      updatedAt: docSnap.data().updatedAt?.toDate() || new Date(),
    } as ServiceCategory;
  }

  /**
   * Get flattened list of all services for easier iteration
   */
  async getFlatServiceList(): Promise<FlatService[]> {
    const categories = await this.getAllCategories();
    const flatList: FlatService[] = [];

    categories.forEach(category => {
      category.subcategories?.forEach(subcategory => {
        subcategory.variations.forEach(variation => {
          flatList.push({
            id: variation.id,
            categoryName: category.name,
            subcategoryName: subcategory.name,
            variationName: variation.name,
            description: variation.description,
            price: variation.price,
            duration: variation.duration,
            imageUrl: variation.imageUrl,
            active: variation.active,
          });
        });
      });
    });

    return flatList;
  }

  /**
   * Find a service variation by NAME (case-insensitive) across flat + hierarchical categories.
   * Returns the minimal shape the emergency rule needs (id/name/allowEmergencySqueeze), or null.
   * Used by the emergency restricted-service gate (task #46 Stage 3, Jose 2026-10-02).
   */
  async findServiceByName(name: string): Promise<{ id: string; name: string; allowEmergencySqueeze?: boolean } | null> {
    const key = (name || '').toLowerCase().trim();
    if (!key) return null;
    const categories = await this.getAllCategories();
    for (const cat of categories) {
      const pools: ServiceVariation[][] = [cat.variations || [], ...(cat.subcategories || []).map(sc => sc.variations || [])];
      for (const vars of pools) {
        const v = vars.find(x => (x.name || '').toLowerCase().trim() === key);
        if (v) return { id: v.id, name: v.name, allowEmergencySqueeze: v.allowEmergencySqueeze };
      }
    }
    return null;
  }

  /**
   * Upload service variation image to Firebase Storage
   */
  async uploadServiceImage(
    categoryId: string,
    subcategoryId: string | null,
    variationId: string,
    file: File
  ): Promise<string> {
    // Convert ANY upload (incl. iPhone HEIC / PNG / odd formats) → a downscaled, web-safe JPEG before it
    // hits Storage, so it actually renders on the client. This is the "extension issue" fix. (Jose 2026-10-17)
    const webFile = await fileToWebImageFile(file);
    const timestamp = Date.now();
    const fileExtension = webFile.name.split('.').pop();
    const storageRef = ref(
      storage,
      `services/${categoryId}/${subcategoryId || '_flat'}/${variationId}_${timestamp}.${fileExtension}`
    );

    await uploadBytes(storageRef, webFile);
    const downloadURL = await getDownloadURL(storageRef);

    return downloadURL;
  }

  /**
   * Delete service image from Firebase Storage
   */
  async deleteServiceImage(imageUrl: string): Promise<void> {
    if (!imageUrl) return;

    try {
      const storageRef = ref(storage, imageUrl);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Error deleting service image:', error);
    }
  }

  /**
   * Add a new service category with full hierarchy
   */
  async addCategory(categoryData: Omit<ServiceCategory, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(this.categoriesCollection, {
      ...categoryData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    return docRef.id;
  }

  /**
   * Update service category (including subcategories and variations)
   */
  async updateCategory(
    categoryId: string,
    updates: Partial<Omit<ServiceCategory, 'id' | 'createdAt'>>
  ): Promise<void> {
    const docRef = doc(this.categoriesCollection, categoryId);

    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Update specific variation image
   */
  async updateVariationImage(
    categoryId: string,
    subcategoryId: string | null,
    variationId: string,
    imageFile: File
  ): Promise<void> {
    // Get current category
    const category = await this.getCategoryById(categoryId);
    if (!category) throw new Error('Category not found');

    // Flat category: variation lives directly on the category (Beard / Specialty).
    if (!subcategoryId) {
      const variation = category.variations?.find(v => v.id === variationId);
      if (!variation) throw new Error('Variation not found');
      if (variation.imageUrl) await this.deleteServiceImage(variation.imageUrl);
      variation.imageUrl = await this.uploadServiceImage(categoryId, null, variationId, imageFile);
      await this.updateCategory(categoryId, { variations: category.variations || [] });
      return;
    }

    // Hierarchical: find subcategory and variation
    const subcategory = category.subcategories?.find(sc => sc.id === subcategoryId);
    if (!subcategory) throw new Error('Subcategory not found');

    const variation = subcategory.variations.find(v => v.id === variationId);
    if (!variation) throw new Error('Variation not found');

    // Delete old image if exists
    if (variation.imageUrl) {
      await this.deleteServiceImage(variation.imageUrl);
    }

    // Upload new image
    const newImageUrl = await this.uploadServiceImage(categoryId, subcategoryId, variationId, imageFile);

    // Update variation
    variation.imageUrl = newImageUrl;

    // Save updated category
    await this.updateCategory(categoryId, { subcategories: category.subcategories || [] });
  }

  /**
   * Import services from structured data (CSV/JSON)
   */
  async importServices(servicesData: ServiceCategory[]): Promise<void> {
    const promises = servicesData.map(category => this.addCategory(category));
    await Promise.all(promises);
  }

  /**
   * Toggle variation active status
   */
  async toggleVariationActive(
    categoryId: string,
    subcategoryId: string,
    variationId: string,
    active: boolean
  ): Promise<void> {
    const category = await this.getCategoryById(categoryId);
    if (!category) throw new Error('Category not found');

    const subcategory = category.subcategories?.find(sc => sc.id === subcategoryId);
    if (!subcategory) throw new Error('Subcategory not found');

    const variation = subcategory.variations.find(v => v.id === variationId);
    if (!variation) throw new Error('Variation not found');

    variation.active = active;

    await this.updateCategory(categoryId, { subcategories: category.subcategories || [] });
  }

  /**
   * Toggle subcategory active status (affects all variations)
   */
  async toggleSubcategoryActive(
    categoryId: string,
    subcategoryId: string,
    active: boolean
  ): Promise<void> {
    const category = await this.getCategoryById(categoryId);
    if (!category) throw new Error('Category not found');

    const subcategory = category.subcategories?.find(sc => sc.id === subcategoryId);
    if (!subcategory) throw new Error('Subcategory not found');

    subcategory.active = active;
    // Also update all variations
    subcategory.variations.forEach(v => v.active = active);

    await this.updateCategory(categoryId, { subcategories: category.subcategories || [] });
  }

  /**
   * Toggle category active status (affects all subcategories and variations)
   */
  async toggleCategoryActive(categoryId: string, active: boolean): Promise<void> {
    const category = await this.getCategoryById(categoryId);
    if (!category) throw new Error('Category not found');

    category.active = active;
    // Also update all subcategories and variations
    category.subcategories?.forEach(sc => {
      sc.active = active;
      sc.variations.forEach(v => v.active = active);
    });

    await this.updateCategory(categoryId, {
      active,
      subcategories: category.subcategories || []
    });
  }

  /**
   * Subscribe to categories with real-time updates
   */
  subscribeToCategories(callback: (categories: ServiceCategory[]) => void): () => void {
    const q = query(this.categoriesCollection, where('active', '==', true));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const categories = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        } as ServiceCategory))
        .sort((a, b) => a.sortOrder - b.sortOrder);

      callback(categories);
    });

    return unsubscribe;
  }

  /**
   * Update a specific service variation
   */
  async updateVariation(
    variationId: string,
    updates: Partial<Omit<ServiceVariation, 'id'>>
  ): Promise<void> {
    // Find the variation in the hierarchy
    const categories = await this.getAllCategories();

    for (const category of categories) {
      // Check flat variations
      if (category.variations) {
        const variationIndex = category.variations.findIndex(v => v.id === variationId);
        if (variationIndex !== -1) {
          category.variations[variationIndex] = {
            ...category.variations[variationIndex],
            ...updates
          };
          await this.updateCategory(category.id, { variations: category.variations });
          return;
        }
      }

      // Check subcategories
      if (category.subcategories) {
        for (let i = 0; i < category.subcategories.length; i++) {
          const variationIndex = category.subcategories[i].variations.findIndex(v => v.id === variationId);
          if (variationIndex !== -1) {
            category.subcategories[i].variations[variationIndex] = {
              ...category.subcategories[i].variations[variationIndex],
              ...updates
            };
            await this.updateCategory(category.id, { subcategories: category.subcategories });
            return;
          }
        }
      }
    }

    throw new Error(`Variation with ID ${variationId} not found`);
  }

  /**
   * Toggle emergency squeeze-in permission for a service variation
   * Some services (fades, precise work) should not be squeezed into tight gaps
   */
  async updateVariationEmergencySqueeze(
    categoryId: string,
    subcategoryId: string | null,
    variationId: string,
    allowEmergencySqueeze: boolean
  ): Promise<void> {
    const category = await this.getCategoryById(categoryId);
    if (!category) throw new Error('Category not found');

    // Handle flat categories (variations directly on category)
    if (category.isFlat && category.variations) {
      const variation = category.variations.find(v => v.id === variationId);
      if (variation) {
        variation.allowEmergencySqueeze = allowEmergencySqueeze;
        await this.updateCategory(categoryId, { variations: category.variations });
        return;
      }
    }

    // Handle hierarchical categories (variations in subcategories)
    if (subcategoryId && category.subcategories) {
      const subcategory = category.subcategories.find(sc => sc.id === subcategoryId);
      if (!subcategory) throw new Error('Subcategory not found');

      const variation = subcategory.variations.find(v => v.id === variationId);
      if (!variation) throw new Error('Variation not found');

      variation.allowEmergencySqueeze = allowEmergencySqueeze;
      await this.updateCategory(categoryId, { subcategories: category.subcategories });
      return;
    }

    throw new Error('Variation not found');
  }

  /**
   * Add a new service variation under a category (flat) or a subcategory (hierarchical).
   * Returns the generated variation id. Handles both shapes so Beard/Specialty (flat) and
   * Haircuts/Women/Style (nested) all work from the one consolidated editor. (Jose 2026-10-17)
   */
  async addVariation(
    categoryId: string,
    subcategoryId: string | null,
    data: Omit<ServiceVariation, 'id'>
  ): Promise<string> {
    const category = await this.getCategoryById(categoryId);
    if (!category) throw new Error('Category not found');

    const slug = (data.name || 'service').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'service';
    const newId = `${slug}-${Date.now().toString(36)}`;
    const newVariation: ServiceVariation = { ...data, id: newId };

    if (!subcategoryId) {
      // Flat category — append to category.variations
      const variations = category.variations || [];
      variations.push(newVariation);
      await this.updateCategory(categoryId, { variations });
      return newId;
    }

    const subcategory = category.subcategories?.find(sc => sc.id === subcategoryId);
    if (!subcategory) throw new Error('Subcategory not found');
    subcategory.variations.push(newVariation);
    await this.updateCategory(categoryId, { subcategories: category.subcategories || [] });
    return newId;
  }

  /**
   * Delete a service variation (flat or nested). Also removes its Storage image. (Jose 2026-10-17)
   */
  async deleteVariation(
    categoryId: string,
    subcategoryId: string | null,
    variationId: string
  ): Promise<void> {
    const category = await this.getCategoryById(categoryId);
    if (!category) throw new Error('Category not found');

    if (!subcategoryId) {
      const variations = category.variations || [];
      const idx = variations.findIndex(v => v.id === variationId);
      if (idx === -1) throw new Error('Variation not found');
      if (variations[idx].imageUrl) await this.deleteServiceImage(variations[idx].imageUrl!);
      variations.splice(idx, 1);
      await this.updateCategory(categoryId, { variations });
      return;
    }

    const subcategory = category.subcategories?.find(sc => sc.id === subcategoryId);
    if (!subcategory) throw new Error('Subcategory not found');
    const idx = subcategory.variations.findIndex(v => v.id === variationId);
    if (idx === -1) throw new Error('Variation not found');
    if (subcategory.variations[idx].imageUrl) await this.deleteServiceImage(subcategory.variations[idx].imageUrl!);
    subcategory.variations.splice(idx, 1);
    await this.updateCategory(categoryId, { subcategories: category.subcategories || [] });
  }

  /**
   * Delete category
   */
  async deleteCategory(categoryId: string): Promise<void> {
    // Get category to delete all images
    const category = await this.getCategoryById(categoryId);
    if (category && category.subcategories) {
      // Delete all variation images
      for (const subcategory of category.subcategories) {
        for (const variation of subcategory.variations) {
          if (variation.imageUrl) {
            await this.deleteServiceImage(variation.imageUrl);
          }
        }
      }
    }

    const docRef = doc(this.categoriesCollection, categoryId);
    await deleteDoc(docRef);
  }
}

// Export singleton instance
export const hierarchicalServiceDataService = new HierarchicalServiceDataService();
export default hierarchicalServiceDataService;
